import { useMemo } from "react";

import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useFeatureFlagGate } from "~/shared/hooks/use-feature-flag-gate.ts";
import type { Load } from "~/shared/schemas/loads-schema.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types";
import type { GridPrefColumn } from "~/shared/utils/apply-grid-prefs.ts";
import { applyGridPrefs } from "~/shared/utils/apply-grid-prefs.ts";
import { getDefaultShipmentBoardGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { buildLoadDataColumns } from "../utils/build-load-data-columns.tsx";
import { buildLoadIconColumns } from "../utils/build-load-icon-columns.tsx";
import type { LoadCellContextValue } from "../utils/load-cell-context.tsx";

type UseLoadColumnsArgs = {
	isLoading: boolean;
	isInternal?: boolean;
	onSubscribeToNotifications?: (load: Load) => void;
	onDetailsClick?: (load: Load) => void;
	onDocumentClick?: (load: Load) => void;
	columnPrefs?: GridPrefColumn[];
};

export const useLoadColumns = ({
	isLoading,
	isInternal: isInternalOverride,
	onSubscribeToNotifications,
	onDetailsClick,
	onDocumentClick,
	columnPrefs,
}: UseLoadColumnsArgs): {
	allColumns: BoardColumn[];
	visibleColumns: BoardColumn[];
	cellContextValue: LoadCellContextValue;
} => {
	const { isInternal: deploymentIsInternal } = useDeploymentType();
	const { isEnabled: detailsFlyoutEnabled } = useFeatureFlagGate(
		"enableTraxShipmentBoardEllipsis",
	);
	const isInternal = isInternalOverride ?? deploymentIsInternal;

	const allColumns: BoardColumn[] = useMemo(() => {
		const iconColumns = buildLoadIconColumns({
			isInternal,
		});

		const dataColumns = buildLoadDataColumns({
			isInternal,
		});

		const defaults =
			getDefaultShipmentBoardGridPrefs(isInternal)[
				`${GridIdentifiers["Shipment Board"]}`
			]?.columns ?? [];
		const defaultsByField = new Map(defaults.map((p) => [p.columnId, p]));
		const savedByField = new Map(
			(columnPrefs ?? []).map((p) => [p.columnId, p]),
		);

		// Merge defaults with the user's saved prefs. `isVisible`, `widthSize`,
		// and `ordinalPosition` are user preferences and the saved value wins.
		// `visibility` is column metadata (which deployments may see the
		// column at all) — defaults always win so that updates to a column's
		// deployment visibility (e.g. opening `carrierName` / `proNumber` to
		// external users) take effect for users with stale persisted prefs
		// from older defaults.
		const mergedById = new Map<string, GridPrefColumn>();
		for (const def of defaults) {
			const saved = savedByField.get(def.columnId);
			mergedById.set(
				def.columnId,
				saved ? { ...saved, visibility: def.visibility } : def,
			);
		}
		// Preserve any saved entries that aren't present in defaults (forward
		// compat — a saved column id we no longer ship). Keep saved visibility
		// for these since there's no default to defer to.
		for (const saved of savedByField.values()) {
			if (!defaultsByField.has(saved.columnId)) {
				mergedById.set(saved.columnId, saved);
			}
		}
		const shipmentBoardPrefs = [...mergedById.values()];

		return applyGridPrefs(
			[...iconColumns, ...dataColumns],
			shipmentBoardPrefs,
			isInternal,
		);
	}, [isInternal, columnPrefs]);

	const visibleColumns = useMemo(
		() => allColumns.filter((col) => col.show),
		[allColumns],
	);

	// Runtime values for the stable module-level cell renderers in
	// `build-load-data-columns.tsx` / `build-load-icon-columns.tsx`. The
	// caller wraps the grid with `<LoadCellContext.Provider value=...>` so
	// the cell renderer function references can stay identical across
	// renders (which prevents Kendo from unmounting/remounting every cell on
	// column-pref change).
	const cellContextValue = useMemo<LoadCellContextValue>(
		() => ({
			isLoading,
			isInternal,
			detailsFlyoutEnabled,
			onDetailsClick,
			onDocumentClick,
			onSubscribeToNotifications,
		}),
		[
			isLoading,
			isInternal,
			detailsFlyoutEnabled,
			onDetailsClick,
			onDocumentClick,
			onSubscribeToNotifications,
		],
	);

	return { allColumns, visibleColumns, cellContextValue };
};
